import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Plus, Clock, Target, TrendingUp, Zap, Mountain, Activity, X, Trash2, Pencil, BarChart3, Flame, Timer, Bell, BellOff } from 'lucide-react';
import { toast } from 'sonner';
import TopNav from '@/components/TopNav';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Program type colors and styling
const PROGRAM_TYPES = {
  interval: { color: 'bg-orange-500', lightBg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-600', icon: Zap },
  pyramid: { color: 'bg-purple-500', lightBg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-600', icon: Mountain },
  continuous: { color: 'bg-green-500', lightBg: 'bg-green-50', border: 'border-green-200', text: 'text-green-600', icon: TrendingUp },
  custom: { color: 'bg-blue-500', lightBg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-600', icon: Activity }
};

export const PRE_LOADED_PROGRAMS = [
  {
    id: 'head_race_prep_3x20',
    name: 'The 3x20 Minute Head Race Prep',
    description: '3 x 20:00 building rate 20 / 22 / 24 spm, 5:00 rest between',
    type: 'interval',
    icon: Timer,
    intervals: [
      { type: 'work', time: 1200, targetPace: 125, targetSPM: 20 },
      { type: 'rest', time: 300 },
      { type: 'work', time: 1200, targetPace: 122, targetSPM: 22 },
      { type: 'rest', time: 300 },
      { type: 'work', time: 1200, targetPace: 119, targetSPM: 24 }
    ]
  },
  {
    id: 'pyramid_rate_builder_2x30',
    name: "The Pyramid Rate Builder (2x30')",
    description: "2 x 30:00, each 10' @ r18 / 10' @ r20 / 10' @ r22, 5:00 rest between",
    type: 'pyramid',
    icon: Mountain,
    intervals: [
      { type: 'work', time: 600, targetPace: 128, targetSPM: 18 },
      { type: 'work', time: 600, targetPace: 125, targetSPM: 20 },
      { type: 'work', time: 600, targetPace: 122, targetSPM: 22 },
      { type: 'rest', time: 300 },
      { type: 'work', time: 600, targetPace: 128, targetSPM: 18 },
      { type: 'work', time: 600, targetPace: 125, targetSPM: 20 },
      { type: 'work', time: 600, targetPace: 122, targetSPM: 22 }
    ]
  },
  {
    id: 'intervals_4x1000',
    name: '4 x 1000m Intervals',
    description: 'Classic interval workout with 1000m pieces',
    type: 'interval',
    icon: Zap,
    intervals: [
      { type: 'work', distance: 1000, targetPace: 120, targetSPM: 24 },
      { type: 'rest', time: 180 },
      { type: 'work', distance: 1000, targetPace: 120, targetSPM: 24 },
      { type: 'rest', time: 180 },
      { type: 'work', distance: 1000, targetPace: 120, targetSPM: 24 },
      { type: 'rest', time: 180 },
      { type: 'work', distance: 1000, targetPace: 120, targetSPM: 24 }
    ]
  },
  {
    id: 'pyramid_500_1500',
    name: 'Pyramid 500-1500-500',
    description: 'Build up and back down pyramid',
    type: 'pyramid',
    icon: Mountain,
    intervals: [
      { type: 'work', distance: 500, targetPace: 115, targetSPM: 26 },
      { type: 'rest', time: 120 },
      { type: 'work', distance: 1000, targetPace: 118, targetSPM: 24 },
      { type: 'rest', time: 180 },
      { type: 'work', distance: 1500, targetPace: 120, targetSPM: 22 },
      { type: 'rest', time: 180 },
      { type: 'work', distance: 1000, targetPace: 118, targetSPM: 24 },
      { type: 'rest', time: 120 },
      { type: 'work', distance: 500, targetPace: 115, targetSPM: 26 }
    ]
  },
  {
    id: 'steady_state_10k',
    name: '10k Steady State',
    description: 'Continuous 10,000m at steady pace',
    type: 'continuous',
    icon: TrendingUp,
    intervals: [
      { type: 'work', distance: 10000, targetPace: 125, targetSPM: 20 }
    ]
  },
  {
    id: 'sprint_8x500',
    name: '8 x 500m Sprints',
    description: 'High intensity 500m pieces',
    type: 'interval',
    icon: Zap,
    intervals: [
      { type: 'work', distance: 500, targetPace: 110, targetSPM: 28 },
      { type: 'rest', time: 120 },
      { type: 'work', distance: 500, targetPace: 110, targetSPM: 28 },
      { type: 'rest', time: 120 },
      { type: 'work', distance: 500, targetPace: 110, targetSPM: 28 },
      { type: 'rest', time: 120 },
      { type: 'work', distance: 500, targetPace: 110, targetSPM: 28 },
      { type: 'rest', time: 120 },
      { type: 'work', distance: 500, targetPace: 110, targetSPM: 28 },
      { type: 'rest', time: 120 },
      { type: 'work', distance: 500, targetPace: 110, targetSPM: 28 },
      { type: 'rest', time: 120 },
      { type: 'work', distance: 500, targetPace: 110, targetSPM: 28 },
      { type: 'rest', time: 120 },
      { type: 'work', distance: 500, targetPace: 110, targetSPM: 28 }
    ]
  },
  {
    id: 'time_trial_2k',
    name: '2000m Time Trial',
    description: 'All-out 2k test',
    type: 'continuous',
    icon: Target,
    intervals: [
      { type: 'work', distance: 2000, targetPace: 105, targetSPM: 32 }
    ]
  }
];

export default function TrainingPrograms() {
  const navigate = useNavigate();
  const [showCustomBuilder, setShowCustomBuilder] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null); // Track if editing existing program
  const [customPrograms, setCustomPrograms] = useState([]);
  const [customProgram, setCustomProgram] = useState({
    name: '',
    description: '',
    intervals: []
  });
  const [newInterval, setNewInterval] = useState({
    type: 'work',
    measureType: 'distance', // 'distance', 'time', or 'strokes'
    distance: 1000,
    time: 0,
    timeUnit: 'sec', // 'sec' or 'min'
    strokes: 0,
    targetPace: 120,
    targetSPM: 24,
    soundEnabled: true // Bell sound on by default
  });

  // Fetch custom programs from backend
  useEffect(() => {
    const fetchCustomPrograms = async () => {
      try {
        const response = await fetch(`${API}/programs`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const programs = await response.json();
          setCustomPrograms(programs);
        } else {
          // Fallback to localStorage if not authenticated
          const localPrograms = JSON.parse(localStorage.getItem('custom_programs') || '[]');
          setCustomPrograms(localPrograms);
        }
      } catch (error) {
        console.error('Error fetching programs:', error);
        // Fallback to localStorage
        const localPrograms = JSON.parse(localStorage.getItem('custom_programs') || '[]');
        setCustomPrograms(localPrograms);
      }
    };
    
    fetchCustomPrograms();
  }, []);

  // Get color for program type
  const getProgramColor = (program) => {
    const type = program.type || 'custom';
    const colors = {
      interval: '#F97316', // Orange
      pyramid: '#A855F7',  // Purple
      continuous: '#22C55E', // Green
      custom: '#3B82F6'    // Blue
    };
    return colors[type] || colors.custom;
  };

  const startProgram = (program) => {
    // Remove non-serializable properties (like icon components) before storing/navigating
    const serializableProgram = {
      id: program.id || program.program_id,
      program_id: program.program_id || program.id,
      name: program.name,
      description: program.description,
      type: program.type,
      intervals: program.intervals
    };
    
    localStorage.setItem('active_program', JSON.stringify(serializableProgram));
    toast.success(`Loading: ${program.name}`);
    navigate('/program-monitor', { state: { program: serializableProgram } });
  };

  const editProgram = (program, isPreloaded = false) => {
    // Load program data into the builder for editing
    setCustomProgram({
      name: isPreloaded ? `${program.name} (Modified)` : program.name,
      description: program.description || '',
      intervals: [...program.intervals] // Clone intervals
    });
    setEditingProgram(isPreloaded ? null : (program.program_id || program.id)); // Track editing for custom programs
    setShowCustomBuilder(true);
  };

  const addInterval = () => {
    // Convert time to seconds if measured in minutes
    const intervalToAdd = { ...newInterval };
    if ((newInterval.measureType === 'time' || (newInterval.type === 'rest' && newInterval.measureType === 'time')) && newInterval.timeUnit === 'min') {
      intervalToAdd.time = newInterval.time * 60; // Convert minutes to seconds
    }
    // Don't store timeUnit in the interval (it's always stored in seconds)
    delete intervalToAdd.timeUnit;
    
    setCustomProgram({
      ...customProgram,
      intervals: [...customProgram.intervals, intervalToAdd]
    });
    // Reset to defaults - rest defaults to time, work defaults to distance
    setNewInterval({
      type: 'work',
      measureType: 'distance',
      distance: 1000,
      time: 0,
      timeUnit: 'sec',
      strokes: 0,
      targetPace: 120,
      targetSPM: 24,
      soundEnabled: true
    });
  };

  const toggleIntervalSound = (index) => {
    const newIntervals = [...customProgram.intervals];
    newIntervals[index] = {
      ...newIntervals[index],
      soundEnabled: newIntervals[index].soundEnabled === false ? true : false
    };
    setCustomProgram({ ...customProgram, intervals: newIntervals });
  };

  const removeInterval = (index) => {
    const newIntervals = customProgram.intervals.filter((_, i) => i !== index);
    setCustomProgram({ ...customProgram, intervals: newIntervals });
  };

  const saveCustomProgram = async () => {
    if (!customProgram.name) {
      toast.error('Please enter a program name');
      return;
    }
    if (customProgram.intervals.length === 0) {
      toast.error('Please add at least one interval');
      return;
    }

    try {
      // If editing existing custom program, delete old one first
      if (editingProgram) {
        await fetch(`${API}/programs/${editingProgram}`, {
          method: 'DELETE',
          credentials: 'include'
        });
        setCustomPrograms(prev => prev.filter(p => (p.program_id || p.id) !== editingProgram));
      }
      
      // Save to backend
      const response = await fetch(`${API}/programs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: customProgram.name,
          description: customProgram.description,
          intervals: customProgram.intervals
        })
      });
      
      if (response.ok) {
        const savedProgram = await response.json();
        setCustomPrograms(prev => [savedProgram, ...prev]);
        toast.success(editingProgram ? 'Program updated!' : 'Custom program saved!');
      } else {
        // Fallback to localStorage if save failed
        const savedPrograms = JSON.parse(localStorage.getItem('custom_programs') || '[]');
        const filteredPrograms = editingProgram 
          ? savedPrograms.filter(p => p.id !== editingProgram)
          : savedPrograms;
        
        const newProgram = {
          ...customProgram,
          id: `custom_${Date.now()}`,
          icon: Activity
        };
        filteredPrograms.push(newProgram);
        localStorage.setItem('custom_programs', JSON.stringify(filteredPrograms));
        setCustomPrograms(filteredPrograms);
        toast.success(editingProgram ? 'Program updated!' : 'Custom program saved locally!');
      }
      
      setShowCustomBuilder(false);
      setEditingProgram(null);
      setCustomProgram({ name: '', description: '', intervals: [] });
    } catch (error) {
      console.error('Error saving program:', error);
      toast.error('Error saving program');
    }
  };

  const deleteCustomProgram = async (programId) => {
    try {
      await fetch(`${API}/programs/${programId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      setCustomPrograms(prev => prev.filter(p => (p.program_id || p.id) !== programId));
      toast.success('Program deleted');
    } catch (error) {
      console.error('Error deleting program:', error);
      toast.error('Error deleting program');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPace = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate total distance and time for a program
  const getProgramStats = (program) => {
    let totalDistance = 0;
    let totalTime = 0;
    let workIntervals = 0;
    
    program.intervals.forEach(interval => {
      if (interval.type === 'work') {
        totalDistance += interval.distance || 0;
        workIntervals++;
        // Estimate time based on pace (seconds per 500m)
        if (interval.time && !interval.distance) {
          totalTime += interval.time;
          if (interval.targetPace) totalDistance += Math.round((interval.time / interval.targetPace) * 500);
        } else if (interval.targetPace && interval.distance) {
          totalTime += (interval.distance / 500) * interval.targetPace;
        }
      } else if (interval.type === 'rest') {
        // Rest can be time, distance, or strokes based
        if (interval.time) {
          totalTime += interval.time;
        } else if (interval.distance) {
          // Estimate rest time at slower pace (150 s/500m)
          totalTime += (interval.distance / 500) * 150;
        } else if (interval.strokes) {
          // Estimate ~3 seconds per stroke at rest
          totalTime += interval.strokes * 3;
        }
      }
    });
    
    return { totalDistance, totalTime, workIntervals };
  };

  // Get all programs combined
  const allPrograms = [...PRE_LOADED_PROGRAMS, ...customPrograms.map(p => ({ ...p, type: 'custom' }))];
  
  // Calculate summary stats
  const totalPrograms = allPrograms.length;
  const intervalPrograms = allPrograms.filter(p => p.type === 'interval').length;
  const continuousPrograms = allPrograms.filter(p => p.type === 'continuous').length;
  const customCount = customPrograms.length;

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-900 flex flex-col transition-colors">
      <TopNav />
      <div className="flex-1 py-6 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-extrabold tracking-tighter text-zinc-900 dark:text-white" style={{ fontFamily: 'Archivo, sans-serif' }}>
                Training Programs
              </h1>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">Pre-loaded and custom workout programs</p>
            </div>
            <button
              onClick={() => setShowCustomBuilder(!showCustomBuilder)}
              data-testid="create-program-button"
              className="px-5 py-2.5 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-xl font-bold text-sm hover:bg-zinc-800 dark:hover:bg-zinc-100 transition-all hover:scale-105 active:scale-95 flex items-center gap-2 shadow-lg"
            >
              <Plus className="w-4 h-4" />
              Create Custom
            </button>
          </div>

          {/* Summary Cards - Like History Page */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-zinc-100 dark:bg-zinc-700 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-zinc-600 dark:text-zinc-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-zinc-800 dark:text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {totalPrograms}
                  </div>
                  <div className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Total</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-zinc-800 border-2 border-orange-200 dark:border-orange-800 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-orange-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {intervalPrograms}
                  </div>
                  <div className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Interval</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-zinc-800 border-2 border-green-200 dark:border-green-800 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {continuousPrograms}
                  </div>
                  <div className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Steady</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-zinc-800 border-2 border-blue-200 dark:border-blue-800 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                  <Activity className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {customCount}
                  </div>
                  <div className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Custom</div>
                </div>
              </div>
            </div>
          </div>

          {showCustomBuilder && (
            <div className="bg-white dark:bg-zinc-800 border-2 border-blue-200 dark:border-blue-700 rounded-xl shadow-sm p-6 mb-6" data-testid="custom-program-builder">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                    <Plus className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h2 className="text-xl font-bold text-zinc-900 dark:text-white" style={{ fontFamily: 'Archivo, sans-serif' }}>
                    {editingProgram ? 'Edit Program' : 'Create Custom Program'}
                  </h2>
                </div>
                <button onClick={() => setShowCustomBuilder(false)} className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded-lg">
                  <X className="w-5 h-5 text-zinc-500 dark:text-zinc-400" />
                </button>
              </div>
              
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-2 text-zinc-500 dark:text-zinc-400">Program Name</label>
                  <input
                    type="text"
                    value={customProgram.name}
                    onChange={(e) => setCustomProgram({ ...customProgram, name: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-zinc-200 rounded-lg focus:border-zinc-400 focus:outline-none bg-zinc-50"
                    placeholder="My Custom Workout"
                    data-testid="program-name-input"
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-2 text-zinc-500">Description</label>
                  <input
                    type="text"
                    value={customProgram.description}
                    onChange={(e) => setCustomProgram({ ...customProgram, description: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-zinc-200 rounded-lg focus:border-zinc-400 focus:outline-none bg-zinc-50"
                    placeholder="Describe your workout..."
                    data-testid="program-description-input"
                  />
                </div>
              </div>

              <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500 mb-3">Add Interval</h3>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-2 text-zinc-400">Type</label>
                  <select
                    value={newInterval.type}
                    onChange={(e) => {
                      const newType = e.target.value;
                      // Set default measureType based on interval type
                      const defaultMeasure = newType === 'rest' ? 'time' : 'distance';
                      setNewInterval({ 
                        ...newInterval, 
                        type: newType,
                        measureType: defaultMeasure,
                        time: newType === 'rest' ? 60 : 0 // Default 60 seconds for rest
                      });
                    }}
                    className="w-full px-3 py-2.5 border-2 border-zinc-200 rounded-lg focus:border-zinc-400 focus:outline-none bg-zinc-50 text-sm"
                  >
                    <option value="work">Work</option>
                    <option value="rest">Rest</option>
                  </select>
                </div>
                
                {newInterval.type === 'work' && (
                  <>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider mb-2 text-zinc-400">Measure By</label>
                      <select
                        value={newInterval.measureType}
                        onChange={(e) => setNewInterval({ ...newInterval, measureType: e.target.value })}
                        className="w-full px-3 py-2.5 border-2 border-zinc-200 rounded-lg focus:border-zinc-400 focus:outline-none bg-zinc-50 text-sm"
                      >
                        <option value="distance">Distance</option>
                        <option value="time">Time</option>
                        <option value="strokes">Strokes</option>
                      </select>
                    </div>
                    
                    {newInterval.measureType === 'distance' && (
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider mb-2 text-zinc-400">Distance (m)</label>
                        <input
                          type="number"
                          value={newInterval.distance}
                          onChange={(e) => setNewInterval({ ...newInterval, distance: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2.5 border-2 border-zinc-200 rounded-lg focus:border-zinc-400 focus:outline-none bg-zinc-50 text-sm"
                          placeholder="1000"
                        />
                      </div>
                    )}
                    
                    {newInterval.measureType === 'time' && (
                      <div className="col-span-2 md:col-span-1">
                        <label className="block text-xs font-bold uppercase tracking-wider mb-2 text-zinc-400">Time</label>
                        <div className="flex gap-2">
                          <input
                            type="number"
                            value={newInterval.time}
                            onChange={(e) => setNewInterval({ ...newInterval, time: parseInt(e.target.value) || 0 })}
                            className="flex-1 px-3 py-2.5 border-2 border-zinc-200 rounded-lg focus:border-zinc-400 focus:outline-none bg-zinc-50 text-sm"
                            placeholder={newInterval.timeUnit === 'min' ? '5' : '300'}
                            data-testid="time-input"
                          />
                          <select
                            value={newInterval.timeUnit}
                            onChange={(e) => setNewInterval({ ...newInterval, timeUnit: e.target.value })}
                            className="w-20 px-2 py-2.5 border-2 border-zinc-200 rounded-lg focus:border-zinc-400 focus:outline-none bg-zinc-50 text-sm font-bold"
                            data-testid="time-unit-select"
                          >
                            <option value="sec">SEC</option>
                            <option value="min">MIN</option>
                          </select>
                        </div>
                      </div>
                    )}
                    
                    {newInterval.measureType === 'strokes' && (
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider mb-2 text-zinc-400">Strokes</label>
                        <input
                          type="number"
                          value={newInterval.strokes}
                          onChange={(e) => setNewInterval({ ...newInterval, strokes: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2.5 border-2 border-zinc-200 rounded-lg focus:border-zinc-400 focus:outline-none bg-zinc-50 text-sm"
                          placeholder="20"
                        />
                      </div>
                    )}
                    
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider mb-2 text-zinc-400">Pace (s/500m)</label>
                      <input
                        type="number"
                        value={newInterval.targetPace}
                        onChange={(e) => setNewInterval({ ...newInterval, targetPace: parseInt(e.target.value) || 0 })}
                        className="w-full px-3 py-2.5 border-2 border-zinc-200 rounded-lg focus:border-zinc-400 focus:outline-none bg-zinc-50 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider mb-2 text-zinc-400">SPM</label>
                      <input
                        type="number"
                        value={newInterval.targetSPM}
                        onChange={(e) => setNewInterval({ ...newInterval, targetSPM: parseInt(e.target.value) || 0 })}
                        className="w-full px-3 py-2.5 border-2 border-zinc-200 rounded-lg focus:border-zinc-400 focus:outline-none bg-zinc-50 text-sm"
                      />
                    </div>
                  </>
                )}
                
                {newInterval.type === 'rest' && (
                  <>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider mb-2 text-zinc-400">Measure By</label>
                      <select
                        value={newInterval.measureType}
                        onChange={(e) => setNewInterval({ ...newInterval, measureType: e.target.value })}
                        className="w-full px-3 py-2.5 border-2 border-zinc-200 rounded-lg focus:border-zinc-400 focus:outline-none bg-zinc-50 text-sm"
                      >
                        <option value="time">Time</option>
                        <option value="distance">Distance</option>
                        <option value="strokes">Strokes</option>
                      </select>
                    </div>
                    
                    {newInterval.measureType === 'time' && (
                      <div className="col-span-1">
                        <label className="block text-xs font-bold uppercase tracking-wider mb-2 text-zinc-400">Rest Time</label>
                        <div className="flex gap-2">
                          <input
                            type="number"
                            value={newInterval.time}
                            onChange={(e) => setNewInterval({ ...newInterval, time: parseInt(e.target.value) || 0 })}
                            className="flex-1 px-3 py-2.5 border-2 border-zinc-200 rounded-lg focus:border-zinc-400 focus:outline-none bg-zinc-50 text-sm"
                            placeholder={newInterval.timeUnit === 'min' ? '3' : '180'}
                            data-testid="rest-time-input"
                          />
                          <select
                            value={newInterval.timeUnit}
                            onChange={(e) => setNewInterval({ ...newInterval, timeUnit: e.target.value })}
                            className="w-20 px-2 py-2.5 border-2 border-zinc-200 rounded-lg focus:border-zinc-400 focus:outline-none bg-zinc-50 text-sm font-bold"
                            data-testid="rest-time-unit-select"
                          >
                            <option value="sec">SEC</option>
                            <option value="min">MIN</option>
                          </select>
                        </div>
                      </div>
                    )}
                    
                    {newInterval.measureType === 'distance' && (
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider mb-2 text-zinc-400">Rest Distance (m)</label>
                        <input
                          type="number"
                          value={newInterval.distance}
                          onChange={(e) => setNewInterval({ ...newInterval, distance: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2.5 border-2 border-zinc-200 rounded-lg focus:border-zinc-400 focus:outline-none bg-zinc-50 text-sm"
                          placeholder="500"
                          data-testid="rest-distance-input"
                        />
                      </div>
                    )}
                    
                    {newInterval.measureType === 'strokes' && (
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider mb-2 text-zinc-400">Rest Strokes</label>
                        <input
                          type="number"
                          value={newInterval.strokes}
                          onChange={(e) => setNewInterval({ ...newInterval, strokes: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2.5 border-2 border-zinc-200 rounded-lg focus:border-zinc-400 focus:outline-none bg-zinc-50 text-sm"
                          placeholder="10"
                          data-testid="rest-strokes-input"
                        />
                      </div>
                    )}
                  </>
                )}
                
                <button
                  onClick={addInterval}
                  className="self-end px-4 py-2.5 bg-zinc-100 hover:bg-zinc-200 rounded-lg font-bold text-sm transition-colors"
                  data-testid="add-interval-button"
                >
                  <Plus className="inline w-4 h-4" />
                </button>
              </div>

              {customProgram.intervals.length > 0 && (
                <div className="space-y-2 mb-6">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500 mb-3">Intervals ({customProgram.intervals.length})</h3>
                  {customProgram.intervals.map((interval, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg"
                      data-testid={`interval-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-bold text-zinc-400">{index + 1}</span>
                        <div className="text-sm">
                          {interval.type === 'work' ? (
                            <span>
                              <span className="font-bold text-green-600">WORK</span>{' '}
                              {interval.measureType === 'distance' && `${interval.distance}m`}
                              {interval.measureType === 'time' && formatTime(interval.time)}
                              {interval.measureType === 'strokes' && `${interval.strokes} strokes`}
                              {!interval.measureType && `${interval.distance}m`}
                              {' '}@ {formatPace(interval.targetPace)}/500m
                            </span>
                          ) : (
                            <span>
                              <span className="font-bold text-blue-600">REST</span>{' '}
                              {interval.measureType === 'distance' && `${interval.distance}m`}
                              {interval.measureType === 'strokes' && `${interval.strokes} strokes`}
                              {(interval.measureType === 'time' || !interval.measureType || interval.time) && formatTime(interval.time)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Bell toggle for interval sound */}
                        <button
                          onClick={() => toggleIntervalSound(index)}
                          className={`p-1.5 rounded transition-colors ${
                            interval.soundEnabled !== false 
                              ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                              : 'bg-zinc-200 text-zinc-400 hover:bg-zinc-300'
                          }`}
                          title={interval.soundEnabled !== false ? 'Sound On - Click to mute' : 'Sound Off - Click to enable'}
                          data-testid={`interval-sound-${index}`}
                        >
                          {interval.soundEnabled !== false ? (
                            <Bell className="w-4 h-4" />
                          ) : (
                            <BellOff className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => removeInterval(index)}
                          className="p-1.5 hover:bg-red-100 rounded text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={saveCustomProgram}
                  className="px-5 py-2.5 bg-zinc-900 text-white rounded-lg font-bold text-sm hover:bg-zinc-800 transition-colors"
                  data-testid="save-program-button"
                >
                  {editingProgram ? 'Update Program' : 'Save Program'}
                </button>
                <button
                  onClick={() => {
                    setShowCustomBuilder(false);
                    setEditingProgram(null);
                    setCustomProgram({ name: '', description: '', intervals: [] });
                  }}
                  className="px-5 py-2.5 bg-zinc-100 text-zinc-700 rounded-lg font-bold text-sm hover:bg-zinc-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Custom Programs - Now at the top */}
          {customPrograms.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ fontFamily: 'Archivo, sans-serif' }}>
                <Activity className="w-5 h-5 text-blue-500" />
                <span className="text-zinc-700">My Custom Programs</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {customPrograms.map((program) => {
                  const stats = getProgramStats(program);
                  return (
                    <div
                      key={program.program_id || program.id}
                      className="bg-white border-2 border-blue-200 rounded-xl shadow-sm p-5 hover:shadow-lg transition-all group"
                      data-testid={`custom-program-${program.program_id || program.id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Activity className="w-6 h-6 text-blue-600" />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 bg-blue-100 text-blue-600 rounded-full">
                            Custom
                          </span>
                          <button
                            onClick={() => editProgram(program, false)}
                            className="p-1.5 hover:bg-zinc-100 rounded-lg text-zinc-400 hover:text-zinc-600 transition-colors"
                            data-testid={`edit-custom-${program.program_id || program.id}`}
                            title="Edit Program"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteCustomProgram(program.program_id || program.id)}
                            className="p-1.5 hover:bg-red-100 rounded-lg text-zinc-400 hover:text-red-500 transition-colors"
                            data-testid={`delete-${program.program_id || program.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <h3 className="text-lg font-bold mb-1 text-zinc-800" style={{ fontFamily: 'Archivo, sans-serif' }}>
                        {program.name}
                      </h3>
                      <p className="text-sm text-zinc-500 mb-3">{program.description}</p>
                      
                      {/* Stats Row */}
                      <div className="flex items-center gap-4 text-xs text-zinc-400 mb-4 pb-4 border-b border-zinc-100">
                        <span className="flex items-center gap-1">
                          <Target className="w-3.5 h-3.5" />
                          {(stats.totalDistance / 1000).toFixed(1)}km
                        </span>
                        <span className="flex items-center gap-1">
                          <Timer className="w-3.5 h-3.5" />
                          ~{Math.round(stats.totalTime / 60)}min
                        </span>
                        <span className="flex items-center gap-1">
                          <Zap className="w-3.5 h-3.5" />
                          {stats.workIntervals} pieces
                        </span>
                      </div>
                      
                      <button
                        onClick={() => startProgram(program)}
                        className="w-full px-4 py-3 bg-blue-500 text-white rounded-xl font-bold text-sm hover:bg-blue-600 transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 shadow-md"
                      >
                        <Play className="w-4 h-4" />
                        Start Program
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Pre-loaded Programs */}
          <div className="mb-8">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ fontFamily: 'Archivo, sans-serif' }}>
              <Flame className="w-5 h-5 text-orange-500" />
              <span className="text-zinc-700">Pre-loaded Programs</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {PRE_LOADED_PROGRAMS.map((program) => {
                const typeStyle = PROGRAM_TYPES[program.type] || PROGRAM_TYPES.interval;
                const Icon = program.icon;
                const stats = getProgramStats(program);
                return (
                  <div
                    key={program.id}
                    className={`bg-white border-2 ${typeStyle.border} rounded-xl shadow-sm p-5 hover:shadow-lg transition-all group`}
                    data-testid={`program-${program.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className={`w-12 h-12 ${typeStyle.lightBg} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                        <Icon className={`w-6 h-6 ${typeStyle.text}`} />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1 ${typeStyle.lightBg} ${typeStyle.text} rounded-full`}>
                          {program.type}
                        </span>
                        <button
                          onClick={() => editProgram(program, true)}
                          className="p-1.5 hover:bg-zinc-100 rounded-lg text-zinc-400 hover:text-zinc-600 transition-colors"
                          data-testid={`edit-${program.id}`}
                          title="Edit & Save as Custom"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <h3 className="text-lg font-bold mb-1 text-zinc-800" style={{ fontFamily: 'Archivo, sans-serif' }}>
                      {program.name}
                    </h3>
                    <p className="text-sm text-zinc-500 mb-3">{program.description}</p>
                    
                    {/* Stats Row */}
                    <div className="flex items-center gap-4 text-xs text-zinc-400 mb-4 pb-4 border-b border-zinc-100">
                      <span className="flex items-center gap-1">
                        <Target className="w-3.5 h-3.5" />
                        {(stats.totalDistance / 1000).toFixed(1)}km
                      </span>
                      <span className="flex items-center gap-1">
                        <Timer className="w-3.5 h-3.5" />
                        ~{Math.round(stats.totalTime / 60)}min
                      </span>
                      <span className="flex items-center gap-1">
                        <Zap className="w-3.5 h-3.5" />
                        {stats.workIntervals} pieces
                      </span>
                    </div>
                    
                    <button
                      onClick={() => startProgram(program)}
                      className={`w-full px-4 py-3 ${typeStyle.color} text-white rounded-xl font-bold text-sm hover:opacity-90 transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 shadow-md`}
                      data-testid={`start-${program.id}`}
                    >
                      <Play className="w-4 h-4" />
                      Start Program
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
